import React, { useState } from "react";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  CalendarDays,
  ClipboardList,
  UserCog,
  FileText,
  Activity,
  Building2,
  Network,
  LogOut,
  Menu,
  X,
  Shield,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";

const navTenant = [
  { to: "/", label: "ダッシュボード", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/patients", label: "利用者管理", icon: Users, testid: "nav-patients" },
  { to: "/schedule", label: "スケジュール", icon: CalendarDays, testid: "nav-schedule" },
  { to: "/records", label: "看護記録", icon: ClipboardList, testid: "nav-records" },
  { to: "/evaluations", label: "評価ツール", icon: Activity, testid: "nav-evaluations" },
  { to: "/documents", label: "書類管理", icon: FileText, testid: "nav-documents" },
  { to: "/staff", label: "スタッフ", icon: UserCog, testid: "nav-staff", roles: ["tenant_admin"] },
  { to: "/referrers", label: "医療機関元", icon: Network, testid: "nav-referrers" },
  { to: "/organization", label: "事業所情報", icon: Building2, testid: "nav-org", roles: ["tenant_admin"] },
];

const navSuper = [
  { to: "/admin", label: "運営管理", icon: Shield, testid: "nav-admin" },
];

export const Layout = () => {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const items = user?.role === "super_admin" ? navSuper : navTenant.filter((i) => !i.roles || i.roles.includes(user?.role));

  const roleLabel = {
    super_admin: "運営者",
    tenant_admin: "管理者",
    nurse: "看護師",
    office: "事務",
  }[user?.role] || "";

  return (
    <div className="min-h-screen flex bg-[#FBFBF9]">
      {/* Mobile header */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-14 bg-white border-b border-[#E5E5E3] flex items-center justify-between px-4 z-40">
        <div className="flex items-center gap-2">
          <button
            data-testid="mobile-menu-toggle"
            className="p-2"
            onClick={() => setOpen(!open)}
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
          <span className="font-bold text-[#2C5545]">訪問看護SaaS</span>
        </div>
        <button data-testid="mobile-logout" onClick={() => { logout(); nav("/login"); }} className="p-2">
          <LogOut size={18} />
        </button>
      </div>

      {/* Sidebar */}
      <aside
        className={`${open ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 fixed md:sticky top-0 left-0 h-screen w-64 bg-white border-r border-[#E5E5E3] z-30 transition-transform flex flex-col`}
      >
        <div className="px-6 py-6 border-b border-[#E5E5E3]">
          <div className="text-xs tracking-[0.2em] uppercase text-[#8A8A86] font-numeric">VNS · SaaS</div>
          <div className="text-lg font-bold text-[#2C5545] mt-1">訪問看護</div>
          <div className="text-sm text-[#4A4A48]">運営システム</div>
        </div>

        <nav className="flex-1 overflow-y-auto py-4">
          {items.map((i) => {
            const Icon = i.icon;
            const active = loc.pathname === i.to || (i.to !== "/" && loc.pathname.startsWith(i.to));
            return (
              <Link
                key={i.to}
                to={i.to}
                data-testid={i.testid}
                onClick={() => setOpen(false)}
                className={`flex items-center gap-3 px-6 py-3 text-sm transition-colors ${
                  active
                    ? "bg-[#D9E2D5] text-[#2C5545] font-medium border-r-2 border-[#2C5545]"
                    : "text-[#4A4A48] hover:bg-[#F3F3F1]"
                }`}
              >
                <Icon size={18} strokeWidth={1.5} />
                {i.label}
              </Link>
            );
          })}
        </nav>

        <div className="border-t border-[#E5E5E3] p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-[#D9E2D5] flex items-center justify-center text-[#2C5545] font-bold">
              {user?.name?.[0] || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-xs text-[#8A8A86]">{roleLabel}</div>
            </div>
          </div>
          <Button
            data-testid="logout-button"
            variant="outline"
            size="sm"
            className="w-full border-[#E5E5E3]"
            onClick={() => { logout(); nav("/login"); }}
          >
            <LogOut size={14} className="mr-2" />
            ログアウト
          </Button>
        </div>
      </aside>

      {open && <div className="md:hidden fixed inset-0 bg-black/30 z-20" onClick={() => setOpen(false)} />}

      <main className="flex-1 md:ml-0 pt-14 md:pt-0 min-w-0">
        <div className="p-4 md:p-8 max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Layout;
